// components/crm/contact/tabs/activityConfig.ts
import {
  UserCog,
  RefreshCw,
  Activity as ActivityIcon,
  Link2,
  Briefcase,
  ArrowRightLeft,
  Building2,
  SquareCheck,
  CheckCircle2,
  Trash2,
  PenLine,
  UserPlus,
  User,
  Circle,
  LucideIcon,
} from 'lucide-react'

interface ActivityVisual {
  icon: LucideIcon
  iconBg: string
  iconColor: string
}

// Falls back to a neutral dot icon for any activityType we don't
// explicitly recognise yet (backend will keep adding new types).
const defaultVisual: ActivityVisual = {
  icon: Circle,
  iconBg: 'bg-primary-variant',
  iconColor: 'text-neutral-faded',
}

export const activityVisualConfig: Record<string, ActivityVisual> = {
  OWNER_CHANGED: { icon: UserCog, iconBg: 'bg-orange-500/10', iconColor: 'text-orange-500' },
  CONTACT_UPDATED: { icon: User, iconBg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  CONTACT_CREATED: { icon: UserPlus, iconBg: 'bg-blue-500/10', iconColor: 'text-blue-500' },

  LIFECYCLE_STAGE_CHANGED: { icon: RefreshCw, iconBg: 'bg-orange-500/10', iconColor: 'text-orange-500' },
  ENGAGEMENT_STATUS_CHANGED: { icon: ActivityIcon, iconBg: 'bg-yellow-500/10', iconColor: 'text-yellow-500' },

  DEAL_ASSOCIATED: { icon: Link2, iconBg: 'bg-purple-500/10', iconColor: 'text-purple-500' },
  DEAL_CREATED: { icon: Briefcase, iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-500' },
  DEAL_STAGE_CHANGED: { icon: ArrowRightLeft, iconBg: 'bg-purple-500/10', iconColor: 'text-purple-500' },

  COMPANY_ASSOCIATED: { icon: Building2, iconBg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  COMPANY_REMOVED: { icon: Building2, iconBg: 'bg-red-500/10', iconColor: 'text-red-500' },

  TASK_CREATED: { icon: SquareCheck, iconBg: 'bg-primary-variant', iconColor: 'text-[#06B6D4]' },
  TASK_COMPLETED: { icon: CheckCircle2, iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-500' },
  TASK_DELETED: { icon: Trash2, iconBg: 'bg-red-500/10', iconColor: 'text-red-500' },

  NOTE_ADDED: { icon: PenLine, iconBg: 'bg-primary-variant', iconColor: 'text-color-accent' },
  NOTE_DELETED: { icon: Trash2, iconBg: 'bg-red-500/10', iconColor: 'text-red-500' },
}

export const getActivityVisual = (activityType: string): ActivityVisual =>
  activityVisualConfig[activityType] ?? defaultVisual

// Only Notes and Tasks currently get the expandable "card" treatment
// (icon + chevron + body + comment box), matching NoteItem/TaskItem.
// Everything else renders as a single log line.
export const isExpandableActivity = (referenceType?: string): boolean =>
  referenceType === 'CRM_CONTACT_NOTE' || referenceType === 'CRM_CONTACT_TASK' || referenceType === 'CRM_DEAL_TASK' || referenceType === 'CRM_DEAL_NOTE' || referenceType === 'CRM_COMPANY_TASK' || referenceType === 'CRM_COMPANY_NOTE'


// Groups are already date-wise from the backend; we only humanise the
// two most recent days and fall back to the backend's own label.
export const getGroupLabel = (group: { date: string; label: string }): string => {
  if (!group.date) return group.label;

  const toKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (group.date === toKey(today)) return 'Today';
  if (group.date === toKey(yesterday)) return 'Yesterday';

  return group.label;
};